import { useEffect } from 'react';
import { toast } from 'react-toastify';
import socket, { connectSocket } from '../../utils/socket';

// Short two-tone "ding" using the Web Audio API (no sound file needed)
function playDing() {
    try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        [880, 1320].forEach((freq, i) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = 'sine';
            osc.frequency.value = freq;
            const start = ctx.currentTime + i * 0.18;
            gain.gain.setValueAtTime(0.0001, start);
            gain.gain.exponentialRampToValueAtTime(0.3, start + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.3);
            osc.connect(gain).connect(ctx.destination);
            osc.start(start);
            osc.stop(start + 0.32);
        });
        setTimeout(() => ctx.close(), 1000);
    } catch (e) {
        // ignore — sound is a nice-to-have
    }
}

/**
 * Alerts support the moment a lead turns 🔥 hot (asked for a call,
 * reserve, book, group of 4+ …): sound + toast + desktop notification.
 * Clicking the toast / notification calls onOpen(chatId).
 */
export default function useHotLeadAlerts(onOpen) {
    // Support screen is open — make sure the live connection is up
    useEffect(() => {
        connectSocket();
    }, []);

    // Ask for desktop-notification permission once (after any click, as
    // browsers require a user gesture)
    useEffect(() => {
        if (!('Notification' in window) || Notification.permission !== 'default') return;
        const ask = () => {
            Notification.requestPermission().catch(() => {});
            window.removeEventListener('click', ask);
        };
        window.addEventListener('click', ask);
        return () => window.removeEventListener('click', ask);
    }, []);

    useEffect(() => {
        const handleHotLead = (lead) => {
            const who = lead?.name || 'Customer';
            const where = lead?.destination ? ` · ${lead.destination}` : '';
            const why = lead?.reason ? `\n${lead.reason}` : '';

            playDing();

            toast.info(`🔥 Hot lead: ${who}${where}${why}`, {
                autoClose: 15000,
                onClick: () => onOpen?.(lead?.chatId)
            });

            if ('Notification' in window && Notification.permission === 'granted' && document.hidden) {
                const n = new Notification(`🔥 Hot lead: ${who}`, {
                    body: `${lead?.reason || 'Wants to be contacted'}${where}`,
                    tag: lead?.chatId
                });
                n.onclick = () => {
                    window.focus();
                    onOpen?.(lead?.chatId);
                    n.close();
                };
            }
        };

        socket.on('hot-lead', handleHotLead);
        return () => socket.off('hot-lead', handleHotLead);
    }, [onOpen]);
}
